import type { DirectiveBinding } from 'vue'

const OBSERVER_KEY = '__lazyObserver__'
const QUEUE_KEY = '__lazyQueued__'
const SETTLE_KEY = '__lazySettle__'
const PRELOAD_MARGIN_PX = 80
/** 一覧で原寸画像が同時に走りすぎないよう制限する */
const MAX_CONCURRENT_LOADS = 6

type QueueItem = {
  el: HTMLElement
  src: string
}

const loadQueue: QueueItem[] = []
let activeLoads = 0

const isNearViewport = (el: HTMLElement) => {
  const rect = el.getBoundingClientRect()
  const viewportHeight = window.innerHeight || document.documentElement.clientHeight
  return rect.bottom >= -PRELOAD_MARGIN_PX && rect.top <= viewportHeight + PRELOAD_MARGIN_PX
}

const applySrc = (el: HTMLElement, src: string) => {
  ;(el as HTMLImageElement | HTMLIFrameElement).src = src
}

const disconnectObserver = (el: HTMLElement) => {
  const observer = (el as any)[OBSERVER_KEY] as IntersectionObserver | undefined
  if (observer) {
    observer.disconnect()
    delete (el as any)[OBSERVER_KEY]
  }
}

const removeFromQueue = (el: HTMLElement) => {
  const index = loadQueue.findIndex((item) => item.el === el)
  if (index >= 0) {
    loadQueue.splice(index, 1)
  }
  delete (el as any)[QUEUE_KEY]
}

const finishLoad = (el: HTMLElement, onDone: () => void) => {
  let settled = false
  const settle = () => {
    if (settled) return
    settled = true
    el.removeEventListener('load', settle)
    el.removeEventListener('error', settle)
    delete (el as any)[SETTLE_KEY]
    onDone()
  }
  ;(el as any)[SETTLE_KEY] = settle
  el.addEventListener('load', settle)
  el.addEventListener('error', settle)
  // iframe / 通信ハング時の救済（キュー詰まり防止）
  window.setTimeout(settle, 8000)
  return settle
}

const pumpQueue = () => {
  while (activeLoads < MAX_CONCURRENT_LOADS && loadQueue.length > 0) {
    const item = loadQueue.shift()
    if (!item) break
    if (!item.el.isConnected) {
      delete (item.el as any)[QUEUE_KEY]
      continue
    }
    delete (item.el as any)[QUEUE_KEY]
    activeLoads += 1
    const settle = finishLoad(item.el, () => {
      activeLoads = Math.max(0, activeLoads - 1)
      pumpQueue()
    })
    applySrc(item.el, item.src)

    // キャッシュ済み画像は load が再発火しないことがある
    if (item.el instanceof HTMLImageElement && item.el.complete) {
      settle()
    }
  }
}

const enqueueLoad = (el: HTMLElement, src: string) => {
  removeFromQueue(el)
  ;(el as any)[QUEUE_KEY] = true
  loadQueue.push({ el, src })
  pumpQueue()
}

const createObserver = (el: HTMLElement, src: string) => {
  if (isNearViewport(el)) {
    enqueueLoad(el, src)
    return
  }

  const options = {
    root: null,
    threshold: 0,
    rootMargin: `${PRELOAD_MARGIN_PX}px`
  }

  const imageObserver = new IntersectionObserver((entries) => {
    entries.forEach((entry) => {
      if (entry.isIntersecting) {
        enqueueLoad(entry.target as HTMLElement, src)
        imageObserver.unobserve(entry.target)
        delete (el as any)[OBSERVER_KEY]
      }
    })
  }, options)

  imageObserver.observe(el)
  ;(el as any)[OBSERVER_KEY] = imageObserver
}

const lazyLoadDirective = {
  mounted(el: HTMLElement, binding: DirectiveBinding) {
    createObserver(el, binding.value)
  },
  updated(el: HTMLElement, binding: DirectiveBinding) {
    if (binding.value !== binding.oldValue) {
      const settle = (el as any)[SETTLE_KEY] as (() => void) | undefined
      settle?.()
      disconnectObserver(el)
      removeFromQueue(el)
      createObserver(el, binding.value)
    }
  },
  unmounted(el: HTMLElement) {
    // 読み込み中に消えると activeLoads が減らず後続が永久待ちになる
    const settle = (el as any)[SETTLE_KEY] as (() => void) | undefined
    settle?.()
    disconnectObserver(el)
    removeFromQueue(el)
  }
}

export default lazyLoadDirective
